import React, { useState } from 'react';
import Grid from '@mui/material/Grid2';

import ModalOrDrawer from "@components/display/ModalOrDrawer";
import SelectStringBox from "@components/input/selects/SelectStringBox";
import SelectObjectBox from "@components/input/selects/SelectObjectBox";
import ButtonBox from "@components/input/buttons/ButtonBox";
import { ABILITY_SCORES_EXPANDED, FEATURE_SOURCE_TYPE_OPTIONS } from '@dndModels/Constants';
import { DataType, isDataType } from '@dndModels/Types';
import SelectSpecialFeatureTypeBox from './select/SelectSpecialFeatureTypeBox';
import SelectSourceBookBox from './select/SelectSourceBookBox';

const REPLACER_TYPES = [
  "Attack String",
  "DC String",
  "Potence String",
  "Average",
  "Class Table",
  "Bonus Spells Table",
  "Rollable Table",
  "Spell List",
  "Spell Descriptions",
];

interface Props {
  open: boolean;
  onClose: () => void;
  onInsert: (value: string) => void;
}

export default function ReplacerSelectorModal({ open, onClose, onInsert }: Props) {
  const [replacer_type, setReplacerType] = useState("");
  const [stat, setStat] = useState("");
  const [x, setX] = useState<string>("");
  const [y, setY] = useState<string>("");
  const [zn_input, setZnInput] = useState<string>("");
  const [zn_list, setZnList] = useState<number[]>([]);
  const [dice_count, setDiceCount] = useState<string>("");
  const [dice_size, setDiceSize] = useState<string>("");
  const [flat_total, setFlatTotal] = useState<string>("");
  const [class_id, setClassId] = useState<string>("");
  const [fs_type, setFsType] = useState<DataType | "">("");
  const [fs_id, setFsId] = useState<string>("");
  const [fs_sub_id, setFsSubId] = useState<string>("Any");
  const [rollable_table_id, setRollableTableId] = useState<string>("");
  const [spell_list_source_book_ids, setSpellListSourceBookIds] = useState<string[]>([]);
  const [spell_list_class_id, setSpellListClassId] = useState<string>("");
  const [spell_descriptions_source_book_id, setSpellDescriptionsSourceBookId] = useState<string>("");

  const resetState = () => {
    setStat("");
    setX("");
    setY("");
    setZnInput("");
    setZnList([]);
    setDiceCount("");
    setDiceSize("");
    setFlatTotal("");
    setClassId("");
    setFsType("");
    setFsId("");
    setFsSubId("Any");
    setRollableTableId("");
    setSpellListSourceBookIds([]);
    setSpellListClassId("");
    setSpellDescriptionsSourceBookId("");
  };

  const handleTypeChange = (changed: string) => {
    setReplacerType(changed);
    resetState();
  };

  const handleFsTypeChange = (changed: string) => {
    if (isDataType(changed)) {
      setFsType(changed);
    }
    setFsSubId("Any");
    setFsId("");
  };

  const handleFSSubIdChange = (changed: string) => {
    setFsSubId(changed);
    setFsId("");
  }

  const addZn = () => {
    const n = parseFloat(zn_input);
    if (!isNaN(n)) {
      setZnList([...zn_list, n]);
      setZnInput("");
    }
  };

  const removeZn = (idx: number) => {
    setZnList(zn_list.filter((_, i) => i !== idx));
  };

  const buildString = (): string => {
    const zn_seg = zn_list.length > 0 ? "." + zn_list.join(".") : "";
    switch (replacer_type) {
      case "Attack String":
        return `{attack_string.${stat}${zn_seg}}`;
      case "DC String":
        return `{dc_string.${stat}${zn_seg}}`;
      case "Potence String":
        return `{potence_string.${stat}.${x}.${y}${zn_seg}}`;
      case "Average":
        return `{avg(${dice_count}d${dice_size}+${flat_total})}`;
      case "Class Table":
        return `{class_table.${class_id}}`;
      case "Bonus Spells Table":
        return `{bonus_spells_table.${fs_type}.${fs_id}}`;
      case "Rollable Table":
        return `{rollable_table.${rollable_table_id}}`;
      case "Spell List":
        return `{spell_list.${spell_list_class_id}${spell_list_source_book_ids.length > 0 ? "." + spell_list_source_book_ids.join(".") : ""}}`;
      case "Spell Descriptions":
        return `{spell_descriptions.${spell_descriptions_source_book_id}}`;
      default:
        return "";
    }
  };

  const isValid = (): boolean => {
    switch (replacer_type) {
      case "Attack String":
      case "DC String":
        return !!stat;
      case "Potence String":
        return !!stat && x !== "" && y !== "";
      case "Average":
        return dice_count !== "" && dice_size !== "" && flat_total !== "";
      case "Class Table":
        return !!class_id;
      case "Bonus Spells Table":
        return !!fs_type && !!fs_id;
      case "Rollable Table":
        return !!rollable_table_id;
      case "Spell List":
        return !!spell_list_source_book_ids && !!spell_list_class_id;
      case "Spell Descriptions":
        return !!spell_descriptions_source_book_id;
      default:
        return false;
    }
  };

  const handleInsert = () => {
    if (!isValid()) return;
    onInsert(buildString());
    onClose();
  };

  const preview = isValid() ? buildString() : "";

  const needsStat = replacer_type === "Attack String" || replacer_type === "DC String" || replacer_type === "Potence String";
  const needsXY = replacer_type === "Potence String";
  const needsZn = replacer_type === "Attack String" || replacer_type === "DC String" || replacer_type === "Potence String";
  const needsAvg = replacer_type === "Average";
  const needsClass = replacer_type === "Class Table";
  const needsBonusSpells = replacer_type === "Bonus Spells Table";
  const needsRollableTable = replacer_type === "Rollable Table";
  const needsSpellList = replacer_type === "Spell List";
  const needsSpellDescriptions = replacer_type === "Spell Descriptions";

  return (
    <ModalOrDrawer open={open} onClose={onClose} width={500}>
      <Grid container spacing={1} direction="row" style={{ padding: "16px" }}>

        <Grid size={12}>
          <SelectStringBox
            name="Replacer Type"
            value={replacer_type}
            options={REPLACER_TYPES}
            onChange={handleTypeChange}
          />
        </Grid>

        {needsStat && (
          <Grid size={12}>
            <SelectStringBox
              name="Ability Score"
              value={stat}
              options={ABILITY_SCORES_EXPANDED}
              onChange={setStat}
            />
          </Grid>
        )}

        {needsXY && (
          <>
            <Grid size={6}>
              <input
                type="number"
                placeholder="x"
                value={x}
                onChange={e => setX(e.target.value)}
                style={numInputStyle}
              />
            </Grid>
            <Grid size={6}>
              <input
                type="number"
                placeholder="y"
                value={y}
                onChange={e => setY(e.target.value)}
                style={numInputStyle}
              />
            </Grid>
          </>
        )}

        {needsZn && (
          <>
            <Grid size={8}>
              <input
                type="number"
                placeholder="Add numeric value"
                value={zn_input}
                onChange={e => setZnInput(e.target.value)}
                onKeyDown={e => { if (e.key === "Enter") addZn(); }}
                style={numInputStyle}
              />
            </Grid>
            <Grid size={4}>
              <ButtonBox name="Add" onClick={addZn} />
            </Grid>
            {zn_list.length > 0 && (
              <Grid size={12}>
                <div style={{ display: "flex", flexWrap: "wrap", gap: "4px" }}>
                  {zn_list.map((n, i) => (
                    <span
                      key={i}
                      onClick={() => removeZn(i)}
                      title="Click to remove"
                      style={znChipStyle}
                    >
                      {n} ×
                    </span>
                  ))}
                </div>
              </Grid>
            )}
          </>
        )}

        {needsAvg && (
          <>
            <Grid size={4}>
              <input
                type="number"
                placeholder="Count"
                value={dice_count}
                onChange={e => setDiceCount(e.target.value)}
                style={numInputStyle}
              />
            </Grid>
            <Grid size={4}>
              <input
                type="number"
                placeholder="Size"
                value={dice_size}
                onChange={e => setDiceSize(e.target.value)}
                style={numInputStyle}
              />
            </Grid>
            <Grid size={4}>
              <input
                type="number"
                placeholder="Flat bonus"
                value={flat_total}
                onChange={e => setFlatTotal(e.target.value)}
                style={numInputStyle}
              />
            </Grid>
          </>
        )}

        {needsClass && (
          <Grid size={12}>
            <SelectObjectBox
              name="Class"
              data_type="game_class"
              value={class_id}
              auto_sort
              onChange={setClassId}
            />
          </Grid>
        )}

        {needsBonusSpells && (
          <>
            <Grid size={12}>
              <SelectStringBox
                name="Feature Source Type"
                value={fs_type}
                options={[...FEATURE_SOURCE_TYPE_OPTIONS,{ id: "special_feature", text: "Special Feature" }]}
                onChange={handleFsTypeChange}
              />
            </Grid>
            {fs_type && 
              <>
                { fs_type === "special_feature" ? 
                  <>
                    <Grid size={12}>
                      <SelectSpecialFeatureTypeBox 
                        name="Special Feature Type"
                        value={fs_sub_id} 
                        allowAny
                        onChange={handleFSSubIdChange}
                      />
                    </Grid>
                    <Grid size={12}>
                      <SelectObjectBox
                        name="Feature Source"
                        data_type="special_feature"
                        filter={ fs_sub_id === "Any" ? {} : { type: fs_sub_id } }
                        value={fs_id}
                        auto_sort
                        onChange={setFsId}
                      />
                    </Grid>
                  </>
                : fs_type === "subclass" ? 
                  <>
                    <Grid size={12}>
                      <SelectObjectBox 
                        name="Of Class"
                        data_type="game_class"
                        extra_options={["Any"]}
                        value={fs_sub_id} 
                        onChange={handleFSSubIdChange}
                      />
                    </Grid>
                    <Grid size={12}>
                      <SelectObjectBox
                        name="Subclass"
                        data_type="subclass"
                        filter={ fs_sub_id === "Any" ? {} : { game_class_id: fs_sub_id } }
                        value={fs_id}
                        auto_sort
                        onChange={setFsId}
                      />
                    </Grid>
                  </>
                :
                  <Grid size={12}>
                    <SelectObjectBox
                      name="Feature Source"
                      data_type={fs_type}
                      value={fs_id}
                      auto_sort
                      onChange={setFsId}
                    />
                  </Grid>
                }
              </>
            }
          </>
        )}

        {needsRollableTable && (
          <Grid size={12}>
            <SelectObjectBox
              name="Rollable Table"
              data_type="rollable_table"
              value={rollable_table_id}
              auto_sort
              onChange={setRollableTableId}
            />
          </Grid>
        )}

        {needsSpellList && (
          <>
            <Grid size={12}>
              <SelectObjectBox
                name="For Class"
                data_type="game_class"
                value={spell_list_class_id}
                auto_sort
                onChange={setSpellListClassId}
              />
            </Grid>
            <Grid size={12}>
              <SelectSourceBookBox
                name="From Source Books"
                ids={spell_list_source_book_ids}
                multiple
                onChangeMultiple={(type, ids) => {setSpellListSourceBookIds(ids) }}
              />
            </Grid>
          </>
        )}

        {needsSpellDescriptions && (
          <Grid size={12}>
            <SelectSourceBookBox
              name="Source Book"
              id={spell_descriptions_source_book_id}
              onChange={(type, id) => { setSpellDescriptionsSourceBookId(id) }}
            />
          </Grid>
        )}

        {preview && (
          <Grid size={12}>
            <div style={previewStyle}>
              {preview}
            </div>
          </Grid>
        )}

        <Grid size={12}>
          <ButtonBox
            name="Insert"
            disabled={!isValid()}
            onClick={handleInsert}
          />
        </Grid>

      </Grid>
    </ModalOrDrawer>
  );
}

const numInputStyle: React.CSSProperties = {
  width: "100%",
  boxSizing: "border-box",
  padding: "6px 8px",
  backgroundColor: "#222",
  color: "white",
  border: "1px solid #555",
  borderRadius: "4px",
  fontSize: "14px",
};

const znChipStyle: React.CSSProperties = {
  backgroundColor: "#333",
  color: "white",
  padding: "2px 8px",
  borderRadius: "12px",
  cursor: "pointer",
  fontSize: "13px",
  border: "1px solid #555",
};

const previewStyle: React.CSSProperties = {
  backgroundColor: "#1a1a1a",
  color: "#9cf",
  padding: "8px 12px",
  borderRadius: "4px",
  fontFamily: "monospace",
  fontSize: "13px",
  wordBreak: "break-all",
  border: "1px solid #444",
};